import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { postService } from '../servce/ApisService';
import { API_ENDPOINTS } from '../api';
import { useForm } from 'react-hook-form';
import { useAuth } from '../auth/AuthContext';
import { useLoader } from '../Loader/LoaderContext';
import { useToaster } from '../Toaster/Toaster';

interface ResetPassword {
  newPassword: string;
  reEnterPassword: string;
}

const ResetPassword: React.FC = () => {
  const { showToast } = useToaster();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid, isSubmitting },
    getValues,
    trigger,
  } = useForm<ResetPassword>({
    mode: 'onChange',
  });

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { logout } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showReEnterPassword, setShowReEnterPassword] = useState(false);
  const { setLoading } = useLoader();
  const [apiError, setApiError] = useState<string | null>(null);

  // Watch password fields
  const newPassword = watch('newPassword');
  const reEnterPassword = watch('reEnterPassword');
  const passwordsMatch = newPassword === reEnterPassword;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleReEnterPasswordVisibility = () => {
    setShowReEnterPassword(!showReEnterPassword);
  };

  const onSubmit = async (data: ResetPassword) => {
    setLoading(true);
    try {
      setApiError(null);
      let payload = {
        newPassword: data.newPassword,
        token: token,
      };
      const response = await postService(
        API_ENDPOINTS.verifyForgetPass,
        payload
      );
      const res = response.data;
      if (response.statusCode == 200) {
        logout();
        navigate('/login');
        showToast('Password Changed Successfully', 'success', '');
      }
    } catch (error: any) {
      setApiError(error.response?.data?.message);
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen flex flex-col md:flex-row'>
      <div
        className='w-full md:w-[45%] h-64 md:h-auto bg-[100%] bg-no-repeat relative'
        style={{
          backgroundImage: 'url(/images/loginImg.png)',
          backgroundSize: '100% 100%',
        }}
      ></div>

      <div className='w-full md:w-[55%] flex flex-col justify-center items-center bg-white p-8'>
        <div className='max-w-lg w-full'>
          <div className='mb-8'>
            <span className='flex items-center mb-6'>
              <div>
                <span
                  className='gradient-text text-3xl'
                  style={{ fontSize: '30px', color: '#ffffffde' }}
                >
                  FertilityIn
                </span>
              </div>
            </span>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='form-group mb-3'>
              <label
                htmlFor='password'
                className='block text-[#374151] font-semibold text-sm'
              >
                New Password
              </label>
              <div className='relative input-group mt-2'>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  {...register('newPassword', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                  className='form-control w-full p-2 rounded-md focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500'
                  placeholder='Enter your new password'
                />
                <span className='absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer'>
                  <img
                    onClick={toggleNewPasswordVisibility}
                    src={
                      !showNewPassword
                        ? 'images/show_pwd.png'
                        : 'images/hide_pwd.png'
                    }
                    alt=''
                  />
                </span>
              </div>
              {errors.newPassword && (
                <p className='text-[#B91C1C]'>{errors.newPassword.message}</p>
              )}
            </div>

            <div className='form-group mb-3'>
              <label
                htmlFor='password'
                className='block text-[#374151] font-semibold text-sm'
              >
                Confirm Password
              </label>
              <div className='relative input-group mt-2'>
                <input
                  type={showReEnterPassword ? 'text' : 'password'}
                  {...register('reEnterPassword', {
                    required: 'Password confirmation is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                    validate: (value) => {
                      const { newPassword } = getValues();
                      return value === newPassword || 'Passwords do not match';
                    },
                  })}
                  className='form-control w-full p-2 rounded-md focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500'
                  placeholder='Confirm your new password'
                />
                <span className='absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer'>
                  <img
                    onClick={toggleReEnterPasswordVisibility}
                    src={
                      !showReEnterPassword
                        ? 'images/show_pwd.png'
                        : 'images/hide_pwd.png'
                    }
                    alt=''
                  />
                </span>
              </div>
              {errors.reEnterPassword && (
                <p className='text-[#B91C1C]'>
                  {errors.reEnterPassword.message}
                </p>
              )}
              {!errors.reEnterPassword &&
                newPassword &&
                reEnterPassword &&
                !passwordsMatch && (
                  <p className='text-[#B91C1C]'>Passwords do not match</p>
                )}
            </div>

            {apiError && (
              <p className='text-sm font-normal text-[#B00A2B] hover:underline mb-4'>
                {apiError}
              </p>
            )}

            <div className='flex justify-between'>
              <button
                type='submit'
                className={`w-full p-3 bg-gradient-to-r from-[#E33E5F] to-[#B00A2B] text-white rounded-md transition ${
                  !isValid || !passwordsMatch
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
                disabled={!isValid || isSubmitting || !passwordsMatch}
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
